import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";

export type StatusTone = "success" | "warning" | "error" | "info" | "neutral" | "brand";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 var(--pulse-color); }
  70% { box-shadow: 0 0 0 5px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

export const useToneColor = () => {
  const theme = useTheme();
  return (tone: StatusTone) =>
    tone === "success"
      ? theme.palette.success.main
      : tone === "warning"
      ? theme.palette.warning.main
      : tone === "error"
      ? theme.palette.error.main
      : tone === "info"
      ? theme.palette.info.main
      : tone === "brand"
      ? theme.palette.secondary.main
      : theme.palette.text.secondary;
};

export const StatusDot: React.FC<{
  tone: StatusTone;
  size?: number;
  pulsing?: boolean;
  color?: string;
  /** Ring instead of a filled dot: queued / not started. No glow and no pulse. */
  hollow?: boolean;
}> = ({ tone, size = 8, pulsing = false, color, hollow = false }) => {
  const resolveTone = useToneColor();
  const toneColor = color ?? resolveTone(tone);
  if (hollow) {
    return (
      <Box
        component="span"
        sx={{
          width: size,
          height: size,
          boxSizing: "border-box",
          borderRadius: "50%",
          flexShrink: 0,
          display: "inline-block",
          border: `1.5px solid ${toneColor}`,
          bgcolor: "transparent",
        }}
      />
    );
  }
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        display: "inline-block",
        bgcolor: toneColor,
        boxShadow: `0 0 ${Math.max(6, size + 1)}px ${alpha(toneColor, 0.75)}`,
        ...(pulsing && {
          "--pulse-color": alpha(toneColor, 0.55),
          animation: `${pulse} 2s ease-out infinite`,
        }),
      }}
    />
  );
};

interface StatusPillProps {
  tone: StatusTone;
  label: React.ReactNode;
  icon?: React.ReactNode;
  pulsing?: boolean;
  size?: "sm" | "md";
  /** Identity color (e.g. a bot) instead of a status tone. */
  color?: string;
  /** Hide the leading dot (plain label badges). */
  dot?: boolean;
  /**
   * 6px ring (1.5px border, transparent, no glow) instead of the filled dot: queued / not started.
   * Pair it with `tone="neutral"`, so the ring is drawn in the text-secondary colour.
   */
  hollowDot?: boolean;
}

/** Soft pill: tinted background, dot (or icon) + label. Never color alone. */
export const StatusPill: React.FC<StatusPillProps> = ({
  tone,
  label,
  icon,
  pulsing,
  size = "md",
  color,
  dot = true,
  hollowDot = false,
}) => {
  const resolveTone = useToneColor();
  const toneColor = color ?? resolveTone(tone);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: size === "sm" ? 1 : 1.25,
        height: size === "sm" ? 22 : 26,
        borderRadius: 999,
        bgcolor: alpha(toneColor, 0.12),
        border: `1px solid ${alpha(toneColor, 0.22)}`,
        color: toneColor,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {icon ?? (dot ? <StatusDot tone={tone} color={color} size={6} pulsing={pulsing} hollow={hollowDot} /> : null)}
      <Typography
        component="span"
        sx={{
          fontSize: "0.75rem",
          fontWeight: 650,
          letterSpacing: "0.02em",
          lineHeight: 1,
          color: "inherit",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default StatusPill;
